import { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link as v4Link } from 'react-router-dom';

import { Text, TextButton } from '@zillow/constellation-10';
import { PostAListingSearchModule } from '@zg-rentals/zrm-marketing-components';

import { RMWContext } from '../../providers/RMWContext';

import { AppSelectors } from '../../selectors';

import useLazyBackgroundImage from '../../hooks/useLazyBackgroundImage';

import accessUtils from '../../utils/accessUtils';

import ZRMHomePageCSEvents from '../../model/ClickstreamEvent/ZRMHomePageCSEvents';

import Images, {
  cta1ListingLandingPageBackgroundImages,
  cta2ListingLandingPageBackgroundImages,
  cta3ListingLandingPageBackgroundImages,
  cta4ListingLandingPageBackgroundImages,
} from '../../images/listingLandingPageV2';

import HeadWrapper from '../../components/HeadWrapper';
import Button from '../../components/LandingPageRefresh/Button';
import Carousel from '../../components/LandingPageRefresh/Carousel';
import Checklist from '../../components/LandingPageRefresh/Checklist';
import ContentWrapper from '../../components/LandingPageRefresh/ContentWrapper';
import CrossPromoContainer from '../../components/LandingPageRefresh/CrossPromoContainer';
import CrossPromoContent from '../../components/LandingPageRefresh/CrossPromoContent';
import Hero from '../../components/LandingPageRefresh/Hero';
import Image from '../../components/LandingPageRefresh/Image';
import ImageBackground from '../../components/LandingPageRefresh/ImageBackground';
import ImageContainer from '../../components/LandingPageRefresh/ImageContainer';
import ImageOverlay from '../../components/LandingPageRefresh/ImageOverlay';
import LazyLoad from '../../components/LazyLoad';
import Link from '../../components/LandingPageRefresh/Link';
import MoreButton from '../../components/LandingPageRefresh/MoreButton';
import PromoBlock from '../../components/LandingPageRefresh/PromoBlock';
import ReviewContent from '../../components/LandingPageRefresh/ReviewContent';
import TextBlock from '../../components/LandingPageRefresh/TextBlock';
import TextContainer from '../../components/LandingPageRefresh/TextContainer';
import Title from '../../components/LandingPageRefresh/Title';
import Tooltip from '../../components/LandingPageRefresh/Tooltip';
import PostingSearchModule from '../../components/PostingSearchModule';

import * as S from './styles';

const pageTitle = 'List Rental Properties For Free - Post a Listing | Zillow Rental Manager';
const pageMetaDescription =
  'Post your rental listings for free on Zillow Rental Manager and quickly rent out your house, apartment or condo through the most visited rental network.';

const ListingLandingPage = () => {
  const { paths } = useContext(RMWContext);

  const isProd = useSelector((state) => state.app.isRunningInProd);
  const user = useSelector((state) => state.user);
  const supportsWebp = useSelector((state) => AppSelectors.makeSupportsWebp(state));
  const appConfig = useSelector((state) => AppSelectors.makeAppConfig(state));
  const cookieEnabledServices = useSelector((state) => AppSelectors.makeCookieEnabledServices(state));

  // Hooks
  const [bottomHeroRef, lazyBottomHeroImage] = useLazyBackgroundImage(
    supportsWebp ? Images.CtaBackgroundWebp : Images.CtaBackground,
  );

  const canAccessZRMMarketingComponents = accessUtils.canAccessZRMMarketingComponents({
    appConfig,
    cookieEnabledServices,
  });

  useEffect(() => {
    window.scroll(0, 0);

    ZRMHomePageCSEvents.ZRMPostAListingPageView({ user });
  }, []);

  return (
    <S.ListingLandingPage
      noPadding={true}
      className="ListingLandingPage"
      data-eventid={ZRMHomePageCSEvents.ZRMPostAListingPageView.data_event_id}
      data-eventdescription="View post a listing page event"
    >
      <HeadWrapper title={pageTitle} description={pageMetaDescription} />
      <Hero.Wrapper backgroundImage={supportsWebp ? Images.HeroImageWebp : Images.HeroImage} alignItems="flex-start">
        {canAccessZRMMarketingComponents ? <PostAListingSearchModule /> : <PostingSearchModule />}
        <MoreButton />
      </Hero.Wrapper>
      <PromoBlock forceAlign="center">
        <Title.H2>Post where renters are searching</Title.H2>
        <TextBlock forceAlign="center" marginTop="20px" padding="0 50px" maxWidth="700px">
          Access the network with over 30 million visitors each month<S.Sup>1</S.Sup> to help you find your perfect
          renter.
        </TextBlock>
      </PromoBlock>

      <PromoBlock>
        <ContentWrapper>
          <TextContainer>
            <Title.H3>Find renters with less hassle</Title.H3>
            <TextBlock>
              List your house, townhome, condo, apartment or room for rent on Zillow Rental Manager and it will appear
              on the most visited rental network.<S.Sup>2</S.Sup>
            </TextBlock>
            <LazyLoad>
              <S.PromoLogos>
                <Image
                  webpSrc={Images.ZillowLogoWebp}
                  webpSrc2x={Images.ZillowLogo2xWebp}
                  src={Images.ZillowLogo}
                  src2x={Images.ZillowLogo2x}
                  alt="Zillow logo"
                />
                <Image
                  webpSrc={Images.TruliaLogoWebp}
                  webpSrc2x={Images.TruliaLogo2xWebp}
                  src={Images.TruliaLogo}
                  src2x={Images.TruliaLogo2x}
                  alt="Trulia logo"
                />
                <Image
                  webpSrc={Images.HotPadsLogoWebp}
                  webpSrc2x={Images.HotPadsLogo2xWebp}
                  src={Images.HotPadsLogo}
                  src2x={Images.HotPadsLogo2x}
                  alt="HotPads logo"
                />
              </S.PromoLogos>
            </LazyLoad>
          </TextContainer>
          <ImageContainer>
            <ImageOverlay align="left">
              <LazyLoad>
                <Image
                  webpSrc={Images.Block2OverlayWebp}
                  webpSrc2x={Images.Block2Overlay2xWebp}
                  src={Images.Block2Overlay}
                  src2x={Images.Block2Overlay2x}
                  alt="Screenshot showing a map with properties listed."
                />
              </LazyLoad>
            </ImageOverlay>
            <LazyLoad>
              <ImageBackground images={cta1ListingLandingPageBackgroundImages} />
            </LazyLoad>
          </ImageContainer>
        </ContentWrapper>
      </PromoBlock>

      <PromoBlock>
        <ContentWrapper>
          <ImageContainer>
            <ImageOverlay align="right">
              <LazyLoad>
                <Image
                  webpSrc={Images.Block3OverlayWebp}
                  webpSrc2x={Images.Block3Overlay2xWebp}
                  src={Images.Block3Overlay}
                  src2x={Images.Block3Overlay2x}
                  alt="Screenshot showing a direct message conversation
                                    between the landlord and the renter."
                />
              </LazyLoad>
            </ImageOverlay>
            <LazyLoad>
              <ImageBackground images={cta2ListingLandingPageBackgroundImages} />
            </LazyLoad>
          </ImageContainer>
          <TextContainer>
            <Title.H3>Listing made simple</Title.H3>
            <TextBlock>
              <b>Create your rental listings</b>
              <br />
              It takes just minutes to create a listing &#8212; simply add property details, upload photos and publish.
            </TextBlock>
            <TextBlock>
              <b>Message interested renters</b>
              <br />
              Communicate with renters using Zillow Rental Manager to stay organized during the tenant search process.
            </TextBlock>
            <TextBlock>
              <b>Reuse your rental posting</b>
              <br />
              Time for new renters? We save your listings so you can republish them with the click of a button.
            </TextBlock>
          </TextContainer>
        </ContentWrapper>
      </PromoBlock>
      <PromoBlock>
        <ContentWrapper>
          <TextContainer>
            <Title.H3>Price your rental</Title.H3>
            <TextBlock>
              Feel confident when you list your property &#8212; we&apos;ll help you price it by integrating your Rent
              Zestimate into the listing and giving you tools to review comparable properties.
            </TextBlock>
            <TextBlock>
              <TextButton asChild>
                <a href="https://www.zillow.com/rental-manager/price-my-rental/">Get your Rent Zestimate</a>
              </TextButton>
            </TextBlock>
          </TextContainer>
          <ImageContainer>
            <ImageOverlay align="left">
              <LazyLoad>
                <Image
                  webpSrc={Images.Block4OverlayWebp}
                  wepbSrc2x={Images.Block4Overlay2xWebp}
                  src={Images.Block4Overlay}
                  src2x={Images.Block4Overlay2x}
                  alt="Screenshot of Rent Zestimate history comparing this home,
                                    the neighborhood, and the city"
                />
              </LazyLoad>
            </ImageOverlay>
            <LazyLoad>
              <ImageBackground images={cta3ListingLandingPageBackgroundImages} />
            </LazyLoad>
          </ImageContainer>
        </ContentWrapper>
      </PromoBlock>
      <PromoBlock>
        <ContentWrapper>
          <ImageContainer>
            <ImageOverlay align="right">
              <LazyLoad>
                <Image
                  webpSrc={Images.Block5OverlayWebp}
                  webpSrc2x={Images.Block5Overlay2xWebp}
                  src={Images.Block5Overlay}
                  src2x={Images.Block5Overlay2x}
                  alt="Screenshot of a background check. Showing Name, last four digits of their
                                        social security number, Date of birth, email, and the status of SSN trace,
                                        eviction search, sex offender search, Global watchlist search, and national
                                        criminal search."
                />
              </LazyLoad>
            </ImageOverlay>
            <LazyLoad>
              <ImageBackground images={cta4ListingLandingPageBackgroundImages} />
            </LazyLoad>
          </ImageContainer>
          <TextContainer>
            <Title.H3>Built-in tenant screening</Title.H3>
            <TextBlock>
              When you list your property with Zillow Rental Manager, you can accept rental applications for free to
              quickly screen prospective tenants with background checks, credit reports and eviction history.
              <Tooltip>
                In some cases, application components, such as background checks or credit reports, are modified to
                reflect state and local legal requirements. See more information{' '}
                <Link.TooltipLink to="https://www.zillow.com/info/limits-rental-applications-reports/">
                  here
                </Link.TooltipLink>
              </Tooltip>
            </TextBlock>
            <TextBlock>
              <TextButton asChild>
                <a href="https://www.zillow.com/z/rental-manager/tenant-screening/">Learn more</a>
              </TextButton>
            </TextBlock>
          </TextContainer>
        </ContentWrapper>
      </PromoBlock>
      <PromoBlock backgroundColor="#F9F9F9">
        <Title.H2>Post a listing in 3 easy steps</Title.H2>
        <CrossPromoContainer margin="40px auto">
          <CrossPromoContent>
            <LazyLoad>
              <Image
                alt="Screenshot showing adding listing details."
                webpSrc={Images.Block6StepOneWebp}
                webpSrc2x={Images.Block6StepOne2xWebp}
                src={Images.Block6StepOne}
                src2x={Images.Block6StepOne2x}
                style={{ paddingBottom: '30px' }}
              />
            </LazyLoad>
            <Title.H4 forceAlign="center" padding="0 54px">
              1. Add property details and images
            </Title.H4>
          </CrossPromoContent>
          <CrossPromoContent>
            <LazyLoad>
              <Image
                alt="Screenshot showing rental price and date available being set."
                webpSrc={Images.Block6StepTwoWebp}
                webpSrc2x={Images.Block6StepTwo2xWebp}
                src={Images.Block6StepTwo}
                src2x={Images.Block6StepTwo2x}
                style={{ paddingBottom: '30px' }}
              />
            </LazyLoad>
            <Title.H4 forceAlign="center" padding="0 54px">
              2. Set your monthly rent price
            </Title.H4>
          </CrossPromoContent>
          <CrossPromoContent>
            <LazyLoad>
              <Image
                webpSrc={Images.Block6StepThreeWebp}
                webpSrc2x={Images.Block6StepThree2xWebp}
                src={Images.Block6StepThree}
                src2x={Images.Block6StepThree2x}
                alt="Screenshot showing an active listing."
                style={{ paddingBottom: '30px' }}
              />
            </LazyLoad>
            <Title.H4 forceAlign="center" padding="0 54px">
              3. Publish and share with renters in your area
            </Title.H4>
          </CrossPromoContent>
        </CrossPromoContainer>
      </PromoBlock>
      <PromoBlock>
        <ContentWrapper>
          <TextContainer>
            <Title.H3>Give your listing the premium experience</Title.H3>
            <TextBlock>
              Want help managing your rental and filling vacancies fast? Upgrade your property to a premium listing
              experience for a one-time fee.
              <div>
                <Checklist>
                  <strong>Increased exposure</strong>
                  <p>Get more eyes on your listing from renters searching in your area.</p>
                </Checklist>
                <Checklist>
                  <strong>Pricing & market insights</strong>
                  <p>Track listing performance data and trends to price your rental competitively.</p>
                </Checklist>
                <Checklist>
                  <strong>Listing recommendations</strong>
                  <p>Get customized tips to help your listing appeal to renters.</p>
                </Checklist>
              </div>
            </TextBlock>
            <TextBlock margin="16px auto 16px auto">
              <Link.PromoLink reactRouterLink={v4Link} to={`${paths.propertiesPage}?postingPath=true`}>
                <Button.Primary>Upgrade to premium</Button.Primary>
              </Link.PromoLink>
            </TextBlock>
          </TextContainer>
          <ImageContainer>
            <LazyLoad>
              <Image
                webpSrc={Images.Block7ImageWebp}
                webpSrc2x={Images.Block7Image2xWebp}
                src={Images.Block7Image}
                src2x={Images.Block7Image2x}
                alt="Placeholder"
              />
            </LazyLoad>
          </ImageContainer>
        </ContentWrapper>
      </PromoBlock>
      <PromoBlock>
        <Title.H2>Here&apos;s what others are saying</Title.H2>
        <Carousel>
          <ReviewContent.Wrapper>
            <ReviewContent.Body>
              &quot;First time using Zillow Rental Manager, and I got more responses than any other ad I placed. Great
              comps for me to check out the value of my rental. Easy to use. I can&apos;t say enough good things about
              Zillow Rental Manager.&quot;
            </ReviewContent.Body>
            <ReviewContent.Byline>Landlord, California, 1 unit</ReviewContent.Byline>
          </ReviewContent.Wrapper>
          <ReviewContent.Wrapper>
            <ReviewContent.Body>
              &quot;Huge response: Ten showings, two fully-qualified prospects and rented within five days.&quot;
            </ReviewContent.Body>
            <ReviewContent.Byline>Landlord, Georgia, 4 units</ReviewContent.Byline>
          </ReviewContent.Wrapper>
          <ReviewContent.Body>
            &quot;It is amazing how easy it was to find a good tenant using Zillow Rental Manager! I really appreciate
            having this tool.&quot;
          </ReviewContent.Body>
          <ReviewContent.Byline>Landlord, Florida, 1 unit</ReviewContent.Byline>
          <ReviewContent.Wrapper>
            <ReviewContent.Body>
              &quot;Zillow Rental Manager makes it easy not only to advertise my listings but to manage the leads for
              those listings!&quot;
            </ReviewContent.Body>
            <ReviewContent.Byline>Landlord, Texas, 7 units</ReviewContent.Byline>
          </ReviewContent.Wrapper>
        </Carousel>
      </PromoBlock>
      <PromoBlock backgroundColor="#F9F9F9">
        <Title.H2>You can do more with Zillow Rental Manager</Title.H2>
        <CrossPromoContainer margin="40px auto">
          <CrossPromoContent border={true} height="264px">
            <LazyLoad>
              <Image
                alt=""
                webpSrc={Images.CrossPromoteOneWebp}
                webpSrc2x={Images.CrossPromoteOne2xWebp}
                src={Images.CrossPromoteOne}
                src2x={Images.CrossPromoteOne2x}
                style={{ paddingBottom: '25px' }}
              />
            </LazyLoad>
            <Title.H4 color="#006AFF" forceAlign="center">
              <TextButton asChild>
                <a href="https://www.zillow.com/z/rental-manager/tenant-screening/">Screen tenants</a>
              </TextButton>
            </Title.H4>
          </CrossPromoContent>
          <CrossPromoContent border={true} height="264px">
            <LazyLoad>
              <Image
                alt=""
                webpSrc={Images.CrossPromoteTwoWebp}
                webpSrc2x={Images.CrossPromoteTwo2xWebp}
                src={Images.CrossPromoteTwo}
                src2x={Images.CrossPromoteTwo2x}
                style={{ paddingBottom: '25px' }}
              />
            </LazyLoad>
            <Title.H4 color="#006AFF" forceAlign="center">
              <TextButton asChild>
                <a href="https://www.zillow.com/z/rental-manager/rental-lease-agreements/">Sign a lease</a>
              </TextButton>
            </Title.H4>
          </CrossPromoContent>
          <CrossPromoContent border={true} height="264px">
            <LazyLoad>
              <Image
                alt=""
                webpSrc={Images.CrossPromoteThreeWebp}
                webpSrc2x={Images.CrossPromoteThree2xWebp}
                src={Images.CrossPromoteThree}
                src2x={Images.CrossPromoteThree2x}
                style={{ paddingBottom: '25px' }}
              />
            </LazyLoad>
            <Title.H4 color="#006AFF" forceAlign="center">
              <TextButton asChild>
                <a href="https://www.zillow.com/z/rental-manager/collect-rent-online/">Collect rent</a>
              </TextButton>
            </Title.H4>
          </CrossPromoContent>
        </CrossPromoContainer>
        <S.CrossPromoPadding>
          <Title.H4 color="#006AFF" forceAlign="center">
            <TextButton asChild>
              <a href={isProd ? 'https://www.zillow.com/rental-manager/' : paths.homePage}>
                Learn about Zillow Rental Manager
              </a>
            </TextButton>
          </Title.H4>
        </S.CrossPromoPadding>
      </PromoBlock>
      <Hero.Wrapper backgroundImage={lazyBottomHeroImage} height="400px" ref={bottomHeroRef}>
        <Hero.Content>
          <Title.H2 color="#fff">List your rental property for free</Title.H2>
          <S.HeroSubtextMaxWidth>
            <TextBlock forceAlign="center" color="#fff" marginTop="20px">
              <Hero.Subtext>
                Whether you manage one property or many, we have the tools to help you find your next renter.
              </Hero.Subtext>
              <Link.PromoLink reactRouterLink={v4Link} to={`${paths.propertiesPage}?postingPath=true`}>
                <Button.Cta>Get started</Button.Cta>
              </Link.PromoLink>
            </TextBlock>
          </S.HeroSubtextMaxWidth>
        </Hero.Content>
      </Hero.Wrapper>
      <PromoBlock>
        <ContentWrapper display="block">
          <Text textStyle="body-xs" asChild>
            <p>
              <S.Sup>1</S.Sup>
              Source: Zillow Internal Data, 2021
            </p>
          </Text>
          <Text textStyle="body-xs" asChild>
            <p>
              <S.Sup>2</S.Sup>
              Source: Comscore Media Metrix®, Custom-defined list: Zillow Rentals, Apartments.com Network, RentPath
              Network, Apartmentlist.com, and Zumper.com, Total Audience, Jan 2022-Mar 2022, U.S.
            </p>
          </Text>
        </ContentWrapper>
      </PromoBlock>
    </S.ListingLandingPage>
  );
};

export { ListingLandingPage };
export default ListingLandingPage;
